import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper
} from '@mui/material';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface TicketTypeTemplate {
  id: string;
  name: string;
  description?: string;
  passkit_template_id: string;
}

interface TemplateField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  position: number;
  order: number;
}

interface EventCreationDialogProps {
  open: boolean;
  onClose: () => void;
  productionId: string;
  productionName: string;
  onSuccess: () => void;
}

const EventCreationDialog: React.FC<EventCreationDialogProps> = ({
  open,
  onClose,
  productionId,
  productionName,
  onSuccess
}) => {
  // Step state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Izaberi Template', 'Popuni Polja'];

  // Template selection
  const [templates, setTemplates] = useState<TicketTypeTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState('');

  // Template fields
  const [templateFields, setTemplateFields] = useState<TemplateField[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [fieldsError, setFieldsError] = useState('');

  // Form data
  const [eventName, setEventName] = useState('');
  const [eventUid, setEventUid] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});

  // Create state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Load templates when dialog opens
  useEffect(() => {
    if (open && productionId) {
      loadTemplates();
    } else {
      // Reset state when dialog closes
      resetDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productionId]);

  const resetDialog = () => {
    setActiveStep(0);
    setTemplates([]);
    setSelectedTemplateId('');
    setTemplateFields([]);
    setEventName('');
    setEventUid('');
    setFieldValues({});
    setTemplatesError('');
    setFieldsError('');
    setCreateError('');
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      setTemplatesError('');

      const response = await passkitAPI.getTicketTypeTemplates(productionId);

      if (response.templates && response.templates.length > 0) {
        setTemplates(response.templates);
      } else {
        setTemplatesError('Nema dostupnih template-a za ovu produkciju. Molimo kreirajte ticket type u PassKit portalu prvo.');
      }
    } catch (error: any) {
      logger.error('Error loading templates:', error);
      setTemplatesError(error.response?.data?.detail || 'Greška pri učitavanju template-a');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadTemplateFields = async (templateId: string) => {
    try {
      setLoadingFields(true);
      setFieldsError('');

      const response = await passkitAPI.getTicketTypeTemplateFields(templateId, productionId);

      if (response.fields && response.fields.length > 0) {
        setTemplateFields(response.fields);
        // Initialize field values
        const initialValues: Record<string, any> = {};
        response.fields.forEach((field: TemplateField) => {
          initialValues[field.name] = '';
        });
        setFieldValues(initialValues);
      } else {
        setTemplateFields([]);
        setFieldsError('Ovaj template nema definisana polja.');
      }
    } catch (error: any) {
      logger.error('Error loading template fields:', error);
      setFieldsError(error.response?.data?.detail || 'Greška pri učitavanju polja template-a');
    } finally {
      setLoadingFields(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      loadTemplateFields(templateId);
    } else {
      setTemplateFields([]);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate template selection
      if (!selectedTemplateId) {
        setTemplatesError('Molimo izaberite template');
        return;
      }
      if (templateFields.length === 0) {
        setFieldsError('Izabrani template nema polja. Molimo izaberite drugi.');
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setCreateError('');

      // Validate required fields
      if (!eventName.trim()) {
        setCreateError('Naziv događaja je obavezan');
        return;
      }

      const requiredFields = templateFields.filter(f => f.required);
      for (const field of requiredFields) {
        if (!fieldValues[field.name] || fieldValues[field.name].toString().trim() === '') {
          setCreateError(`Polje "${field.label}" je obavezno`);
          return;
        }
      }

      // Find selected template to get passkit_template_id
      const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
      if (!selectedTemplate) {
        setCreateError('Greška: Template nije pronađen');
        return;
      }

      // Prepare data - use passkit_template_id instead of ticket type id
      const createData = {
        name: eventName,
        base_ticket_type_id: selectedTemplate.passkit_template_id,
        uid: eventUid || undefined,
        custom_fields: fieldValues
      };

      // Call API
      await passkitAPI.createTicketType(productionId, createData);

      // Success
      onSuccess();
      onClose();
      resetDialog();
    } catch (error: any) {
      logger.error('Error creating ticket type:', error);
      setCreateError(error.response?.data?.detail || 'Greška pri kreiranju događaja');
    } finally {
      setCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        // Template selection
        return (
          <Box sx={{ minHeight: '300px' }}>
            {loadingTemplates ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <>
                {templatesError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {templatesError}
                  </Alert>
                )}

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Izaberi osnovu izgleda</InputLabel>
                  <Select
                    value={selectedTemplateId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    label="Izaberi osnovu izgleda"
                    disabled={templates.length === 0}
                  >
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {loadingFields && (
                  <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                    <CircularProgress size={30} />
                    <Typography sx={{ ml: 2 }}>Učitavam polja template-a...</Typography>
                  </Box>
                )}

                {fieldsError && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {fieldsError}
                  </Alert>
                )}

                {selectedTemplateId && templateFields.length > 0 && (
                  <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="body2">
                      ✓ Template učitan sa {templateFields.length} polja
                    </Typography>
                  </Paper>
                )}
              </>
            )}
          </Box>
        );

      case 1:
        // Form fields
        return (
          <Box sx={{ minHeight: '300px' }}>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError}
              </Alert>
            )}

            <TextField
              fullWidth
              required
              label="Naziv Događaja"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Interni naziv za ovaj događaj"
            />

            <TextField
              fullWidth
              label="UID (Opciono)"
              value={eventUid}
              onChange={(e) => setEventUid(e.target.value)}
              sx={{ mb: 3 }}
              helperText="Korisnički definisan ID (opciono)"
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              Polja Template-a
            </Typography>

            {templateFields.map((field) => (
              <TextField
                key={field.name}
                fullWidth
                required={field.required}
                label={field.label}
                value={fieldValues[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                sx={{ mb: 2 }}
                helperText={field.required ? 'Obavezno polje' : 'Opciono'}
                type={field.type === 'number' ? 'number' : 'text'}
              />
            ))}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={creating}
    >
      <DialogTitle>
        Kreiraj Novi Događaj za: {productionName}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          Otkaži
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={creating}>
            Nazad
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!selectedTemplateId || templateFields.length === 0}
          >
            Dalje
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            variant="contained"
            color="success"
            disabled={creating || !eventName.trim()}
          >
            {creating ? <CircularProgress size={20} /> : 'Kreiraj Događaj'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EventCreationDialog;
